type TypedServerError = Error & { type: string; data?: unknown };

function isTypedServerError(err: unknown): err is TypedServerError {
  return err instanceof Error && typeof (err as TypedServerError).type === 'string';
}

function serializeError(err: unknown): Record<string, unknown> {
  if (isTypedServerError(err)) {
    const serialized: Record<string, unknown> = {
      errorType: err.type,
      message: err.message,
    };
    if (err.data !== undefined) {
      serialized.data = err.data;
    }
    if (err.stack) {
      serialized.stack = err.stack;
    }
    return serialized;
  }

  if (err instanceof Error) {
    return {
      message: err.message,
      name: err.name,
      ...(err.stack ? { stack: err.stack } : {}),
    };
  }

  if (err != null) {
    return { message: String(err) };
  }

  return {};
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return JSON.stringify({
      message: 'Failed to serialize error log payload',
      serializationError: true,
    });
  }
}

type ErrorLogExtra = { event?: string; level?: 'error' | 'info' | 'warn' } & Omit<
  Record<string, unknown>,
  'context' | 'event' | 'level' | 'timestamp'
>;

function writeLog(level: 'error' | 'info' | 'warn', payload: Record<string, unknown>): void {
  const serialized = safeStringify(payload);
  if (level === 'info') {
    console.info(serialized);
    return;
  }
  if (level === 'warn') {
    console.warn(serialized);
    return;
  }
  console.error(serialized);
}

function logWithLevel(
  level: 'error' | 'info' | 'warn',
  context: string,
  err: unknown,
  extra?: ErrorLogExtra,
): void {
  const {
    event: customEvent,
    level: customLevel,
    context: _reservedContext,
    timestamp: _reservedTimestamp,
    ...callerExtra
  } = extra ?? {};

  const payload: Record<string, unknown> = {
    ...callerExtra,
    context,
    event: typeof customEvent === 'string' ? customEvent : 'server_error',
    level: customLevel ?? level,
    timestamp: new Date().toISOString(),
  };

  if (Array.isArray(err)) {
    payload.errors = err.map(serializeError);
  } else {
    Object.assign(payload, serializeError(err));
  }

  writeLog(customLevel ?? level, payload);
}

export const error = (context: string, err: unknown, extra?: ErrorLogExtra): void => {
  logWithLevel('error', context, err, extra);
};

export const warn = (context: string, err: unknown, extra?: ErrorLogExtra): void => {
  logWithLevel('warn', context, err, extra);
};
